import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Download, Building2, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BhpUsoReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  // Using global context for branch filter
  const { selectedBranch } = useApp();
  const [branches, setBranches] = useState([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBranches();
  }, []);

  // Auto-fetch report when filters change
  useEffect(() => {
    fetchReport();
  }, [month, year, selectedBranch, period]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadMenu && !event.target.closest('.relative')) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        year,
        ...(period === 'monthly' && { month }),
        ...(selectedBranch && selectedBranch !== 'all' && { branch_id: selectedBranch })
      };
      
      console.log('BHP/USO fetchReport - selectedBranch:', selectedBranch, 'params:', params);

      const response = await axios.get(`${API_URL}/api/v2/reports/bhp-uso`, { 
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleFilter = () => {
    fetchReport();
  };

  const handleExportCSV = () => {
    if (!reportData) return;

    const headers = ['Cabang', 'Kode', 'Pendapatan Kotor', 'BHP (0.5%)', 'USO (1.25%)', 'Pendapatan Bersih'];
    const rows = reportData.branches?.map(branch => [
      branch.branch_name,
      branch.branch_code,
      branch.gross_revenue.toLocaleString('id-ID'),
      branch.bhp.toLocaleString('id-ID'),
      branch.uso.toLocaleString('id-ID'),
      branch.net_revenue.toLocaleString('id-ID')
    ]);

    rows.push([
      'TOTAL',
      '',
      reportData.totals?.gross_revenue.toLocaleString('id-ID'),
      reportData.totals?.bhp.toLocaleString('id-ID'),
      reportData.totals?.uso.toLocaleString('id-ID'),
      reportData.totals?.net_revenue.toLocaleString('id-ID')
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Laporan_BHP_USO_${reportData.period_label.replace(/\s/g, '_')}.csv`;
    link.click();
    setShowDownloadMenu(false);
  };

  const handleExportExcel = () => {
    if (!reportData) return;

    // Create Excel-compatible HTML table
    let html = '<html><head><meta charset="utf-8"><style>';
    html += 'table {border-collapse: collapse; width: 100%;} ';
    html += 'th, td {border: 1px solid black; padding: 8px; text-align: left;} ';
    html += 'th {background-color: #4F46E5; color: white;} ';
    html += '.total-row {background-color: #EEF2FF; font-weight: bold;} ';
    html += '.text-right {text-align: right;} ';
    html += '</style></head><body>';
    html += `<h2>Laporan BHP/USO Per Cabang</h2>`;
    html += `<p>Periode: ${reportData.period_label}</p>`;
    html += `<p>Tarif: BHP ${reportData.rates?.bhp_rate}% | USO ${reportData.rates?.uso_rate}% </p>`;
    html += '<table>';
    html += '<thead><tr>';
    html += '<th>Cabang</th><th>Kode</th><th>Pendapatan Kotor</th><th>BHP (0.5%)</th>';
    html += '<th>USO (1.25%)</th><th>Pendapatan Bersih</th>';
    html += '</tr></thead><tbody>';

    reportData.branches?.forEach(branch => {
      html += '<tr>';
      html += `<td>${branch.branch_name}</td>`;
      html += `<td>${branch.branch_code}</td>`;
      html += `<td class="text-right">Rp ${branch.gross_revenue.toLocaleString('id-ID')}</td>`;
      html += `<td class="text-right">Rp ${branch.bhp.toLocaleString('id-ID')}</td>`;
      html += `<td class="text-right">Rp ${branch.uso.toLocaleString('id-ID')}</td>`;
      html += `<td class="text-right">Rp ${branch.net_revenue.toLocaleString('id-ID')}</td>`;
      html += '</tr>';
    });

    html += '<tr class="total-row">';
    html += '<td colspan="2">TOTAL</td>';
    html += `<td class="text-right">Rp ${reportData.totals?.gross_revenue.toLocaleString('id-ID')}</td>`;
    html += `<td class="text-right">Rp ${reportData.totals?.bhp.toLocaleString('id-ID')}</td>`;
    html += `<td class="text-right">Rp ${reportData.totals?.uso.toLocaleString('id-ID')}</td>`;
    html += `<td class="text-right">Rp ${reportData.totals?.net_revenue.toLocaleString('id-ID')}</td>`;
    html += '</tr>';
    html += '</tbody></table></body></html>';

    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Laporan_BHP_USO_${reportData.period_label.replace(/\s/g, '_')}.xls`;
    link.click();
    setShowDownloadMenu(false);
  };

  const handlePrint = () => {
    if (!reportData) return;
    window.print();
    setShowDownloadMenu(false);
  };

  return (
    <div className="space-y-4 animate-fade-in p-3 sm:p-4 md:p-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Laporan BHP/USO</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Biaya Hak Penyelenggara & Universal Service Obligation (per Cabang)</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-white dark:bg-slate-800">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {/* Period Type */}
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Periode</label>
              <select
                value={period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="monthly">Bulanan</option>
                <option value="yearly">Tahunan</option>
              </select>
            </div>

            {/* Year */}
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Tahun</label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {[...Array(5)].map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
            </div>

            {/* Month (only for monthly period) */}
            {period === 'monthly' && (
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Bulan</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i, 1).toLocaleString('id-ID', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            )}



            {/* Action Buttons */}
            <div className="flex items-end space-x-2">
              <button
                onClick={handleFilter}
                className="flex-1 h-9 px-3 border border-indigo-500 text-indigo-700 dark:text-indigo-300 bg-white dark:bg-slate-900 text-sm font-medium rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
              >
                Tampilkan
              </button>
              
              {/* Download Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  disabled={!reportData}
                  className="h-9 px-3 border border-emerald-500 text-emerald-700 dark:text-emerald-300 bg-white dark:bg-slate-900 text-sm font-medium rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
                
                {/* Dropdown Menu */}
                {showDownloadMenu && reportData && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50">
                    <div className="py-1">
                      <button
                        onClick={handleExportCSV}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Download CSV</span>
                      </button>
                      
                      <button
                        onClick={handleExportExcel}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Download Excel</span>
                      </button>
                      
                      <button
                        onClick={handlePrint}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        <span>Print / PDF</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Report Content */}
      {!loading && reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">Pendapatan Kotor</p>
                    <p className="text-xl font-semibold text-slate-900 dark:text-slate-100 mt-1 truncate">
                      Rp {reportData.totals?.gross_revenue.toLocaleString('id-ID')}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{reportData.period_label}</p>
                  </div>
                  <div className="p-2 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">Total BHP + USO</p>
                    <p className="text-xl font-semibold text-slate-900 dark:text-slate-100 mt-1 truncate">
                      Rp {(reportData.totals?.bhp + reportData.totals?.uso).toLocaleString('id-ID')}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 truncate">
                      BHP: Rp {reportData.totals?.bhp.toLocaleString('id-ID')} · USO: Rp {reportData.totals?.uso.toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div className="p-2 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                    <Calendar className="w-4 h-4" />
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Table */}
          <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-white dark:bg-slate-800">
            <CardHeader className="py-2.5 px-4 border-b border-slate-100 dark:border-slate-700">
              <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-200">Detail Per Cabang</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Cabang</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Kode</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Pendapatan Kotor</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">BHP (0.5%)</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">USO (1.25%)</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300">Pendapatan Bersih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.branches?.map((branch, index) => (
                      <tr key={branch.branch_id} className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="py-2 px-3 text-xs text-slate-800 dark:text-slate-200 font-medium">{branch.branch_name}</td>
                        <td className="py-2 px-3 text-xs text-slate-600 dark:text-slate-400">{branch.branch_code}</td>
                        <td className="py-2 px-3 text-xs text-slate-800 dark:text-slate-200 text-right font-medium">
                          Rp {branch.gross_revenue.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2 px-3 text-xs text-red-600 dark:text-red-400 text-right">
                          Rp {branch.bhp.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2 px-3 text-xs text-orange-600 dark:text-orange-400 text-right">
                          Rp {branch.uso.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2 px-3 text-xs text-emerald-600 dark:text-emerald-400 text-right font-bold">
                          Rp {branch.net_revenue.toLocaleString('id-ID')}
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-indigo-50 dark:bg-indigo-900/30 font-bold">
                      <td className="py-3 px-3 text-xs text-slate-800 dark:text-slate-100" colSpan="2">TOTAL</td>
                      <td className="py-3 px-3 text-xs text-slate-800 dark:text-slate-100 text-right">
                        Rp {reportData.totals?.gross_revenue.toLocaleString('id-ID')}
                      </td>
                      <td className="py-3 px-3 text-xs text-red-600 dark:text-red-400 text-right">
                        Rp {reportData.totals?.bhp.toLocaleString('id-ID')}
                      </td>
                      <td className="py-3 px-3 text-xs text-orange-600 dark:text-orange-400 text-right">
                        Rp {reportData.totals?.uso.toLocaleString('id-ID')}
                      </td>
                      <td className="py-3 px-3 text-xs text-emerald-600 dark:text-emerald-400 text-right">
                        Rp {reportData.totals?.net_revenue.toLocaleString('id-ID')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Tax Rates Info */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 px-4 py-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">Tarif Pajak</span>
              <div className="flex space-x-4">
                <span className="text-slate-700 dark:text-slate-300">BHP: <span className="font-semibold text-slate-900 dark:text-slate-100">{reportData.rates?.bhp_rate}%</span></span>
                <span className="text-slate-700 dark:text-slate-300">USO: <span className="font-semibold text-slate-900 dark:text-slate-100">{reportData.rates?.uso_rate}%</span></span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BhpUsoReport;
